/**
 * Validates and removes orphaned gift products from cart
 * Executes after DOM ready to clean up gifts without associated normal products
 */
class GiftValidator {
  constructor() {
    this.cartItemsElement = document.querySelector('cart-items') || 
                           document.querySelector('cart-drawer-items');
  }

  /**
   * Fetches current cart data from Shopify API
   * @returns {Promise<Object|null>} Cart data or null if error
   */
  async fetchCartData() {
    try {
      const response = await fetch(`${window.routes.cart_url}.js`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * Checks if a gift product has associated normal products in cart
   * @param {string} giftProductHandle - Handle of the product the gift is for
   * @param {Object} cartData - Current cart data
   * @returns {boolean} True if normal products exist for this gift
   */
  hasAssociatedProducts(giftProductHandle, cartData) {
    return cartData.items.some(item => {
      // Skip gift items
      const isGift = item.properties?._is_gift === 'true' || 
                     item.properties?._is_gift === true;
      if (isGift) return false;
      
      // Check if this normal product gives right to the gift      
      return item.handle === giftProductHandle;
    });
  }

  /**
   * Identifies orphaned gift products (gifts without associated normal products)
   * @param {Object} cartData - Current cart data
   * @returns {string[]} Array of line item keys to remove
   */
  identifyOrphanedGifts(cartData) {
    const orphanedGiftKeys = [];

    cartData.items.forEach(item => {
      const isGift = item.properties?._is_gift === 'true' || 
                     item.properties?._is_gift === true;
      
      if (!isGift) return;

      const giftForProduct = item.properties?._gift_for_product;
      
      if (!giftForProduct) {
        return;
      }

      // Check if there are associated normal products
      const hasNormalProducts = this.hasAssociatedProducts(giftForProduct, cartData);
      
      if (!hasNormalProducts) {
        orphanedGiftKeys.push(item.key);
      }
    });

    return orphanedGiftKeys;
  }

  /**
   * Identifies gift products from inactive promotions
   * @param {Object} cartData - Current cart data
   * @returns {string[]} Array of line item keys to remove
   */
  identifyInactivePromoGifts(cartData) {
    
    // Check if activeDiscountsIdsByMarket exists
    if (!window.activeDiscountsIdsByMarket || !Array.isArray(window.activeDiscountsIdsByMarket)) {
      return [];
    }

    const inactiveGiftKeys = [];

    cartData.items.forEach(item => {
      const isGift = item.properties?._is_gift === 'true' || 
                     item.properties?._is_gift === true;
      
      if (!isGift) return;

      const promoId = item.properties?._id_promotion;
      
      // Skip if no promo ID (shouldn't happen, but defensive)
      if (!promoId) {
        return;
      }

      // Check if promo ID is NOT in active discounts
      const isPromoActive = window.activeDiscountsIdsByMarket.includes(promoId);
      
      if (!isPromoActive) {
        inactiveGiftKeys.push(item.key);
      }
    });

    return inactiveGiftKeys;
  }

  /**
   * Extracts product IDs
   * @param {Array<Object>} products - Array of product objects with id property
   * @returns {string[]} Array of numeric product IDs
   */
  extractProductIds(products) {
    return products.map(p => {
      const idParts = p.id.split('/');
      return idParts[idParts.length - 1];
    });
  }

  /**
   * Checks if a cart item is a gift from a specific promotion
   * @param {Object} item - Cart line item
   * @param {string} promoId - Promotion ID to check against
   * @returns {boolean} True if item is a gift from the specified promotion
   */
  isGiftFromPromo(item, promoId) {
    const itemPromoId = item.properties?._id_promotion;
    const cleanPromoId = promoId.split('/').pop();
    return itemPromoId === cleanPromoId && 
          (item.properties?._is_gift === 'true' || item.properties?._is_gift === true);
  }

  /**
   * Calculates cart total excluding gifts and optionally excluding specific lines
   * @param {Object} cartData - Cart data object
   * @param {string[]} excludedKeys - Array of line item keys to exclude from total
   * @returns {number} Cart total in cents
   */
  calculateCartTotal(cartData, excludedKeys = []) {
    return cartData.items.reduce((total, item) => {
      const isGift = item.properties?._is_gift === 'true' || item.properties?._is_gift === true;
      const isExcluded = excludedKeys.includes(item.key);
      
      if (!isGift && !isExcluded) {
        return total + item.final_line_price;
      }
      return total;
    }, 0);
  }

  /**
   * Validates "one group" discount promotions and identifies gifts to remove
   * @param {Object} cartData - Current cart data
   * @param {string[]} excludedKeys - Keys to exclude from cart total calculation
   * @returns {string[]} Array of gift line item keys that should be removed
   */
  validateOneGroupDiscounts(cartData, excludedKeys = []) {
    const discountsOneGroup = window.activeDiscountsData?.['discounts-giftonegroup'] || [];
    const giftsToRemove = [];

    for (const discountObj of discountsOneGroup) {
      for (const [promoId, discount] of Object.entries(discountObj)) {
        const limitGroup = parseFloat(discount.metafield.limitGroup) * 100;
        const giftProductIds = this.extractProductIds(discount.metafield.selectedProductsGroup);

        const giftsInCart = cartData.items.filter(item => this.isGiftFromPromo(item, promoId));
        const cartTotal = this.calculateCartTotal(cartData, excludedKeys);

        if (cartTotal < limitGroup) {
          giftsInCart.forEach(gift => {
            const giftProductId = gift.product_id.toString();
            if (giftProductIds.includes(giftProductId)) {
              giftsToRemove.push(gift.key);
            }
          });
        }
      }
    }

    return giftsToRemove;
  }

  /**
   * Validates "two groups" discount promotions and identifies gifts to remove
   * @param {Object} cartData - Current cart data
   * @param {string[]} excludedKeys - Keys to exclude from cart total calculation
   * @returns {string[]} Array of gift line item keys that should be removed
   */
  validateTwoGroupsDiscounts(cartData, excludedKeys = []) {
    const discountsTwoGroups = window.activeDiscountsData?.['discounts-gifttwogroups'] || [];
    const giftsToRemove = [];

    for (const discountObj of discountsTwoGroups) {
      for (const [promoId, discount] of Object.entries(discountObj)) {
        const limitFirstGroup = parseFloat(discount.metafield.limitFirstGroup) * 100;
        const limitSecondGroup = parseFloat(discount.metafield.limitSecondGroup) * 100;

        const firstGroupIds = this.extractProductIds(discount.metafield.selectedProductsFirstGroup);
        const secondGroupIds = this.extractProductIds(discount.metafield.selectedProductsSecondGroup);

        const giftsInCart = cartData.items.filter(item => this.isGiftFromPromo(item, promoId));
        const cartTotal = this.calculateCartTotal(cartData, excludedKeys);

        giftsInCart.forEach(gift => {
          const giftProductId = gift.product_id.toString();
          const isFirstGroup = firstGroupIds.includes(giftProductId);
          const isSecondGroup = secondGroupIds.includes(giftProductId);

          if (isFirstGroup && cartTotal < limitFirstGroup) {
            giftsToRemove.push(gift.key);
          }
          if (isSecondGroup && cartTotal < limitSecondGroup) {
            giftsToRemove.push(gift.key);
          }
        });
      }
    }

    return giftsToRemove;
  }

  /**
   * Identifies gifts that no longer meet price limit requirements
   * @param {Object} cartData - Current cart data
   * @param {string[]} excludedKeys - Keys to exclude from cart total calculation
   * @returns {string[]} Array of line item keys to remove
   */
  identifyGiftsOverLimitPrice(cartData, excludedKeys = []) {
    if (!window.activeDiscountsData) {
      return [];
    }

    const oneGroupGifts = this.validateOneGroupDiscounts(cartData, excludedKeys);
    const twoGroupsGifts = this.validateTwoGroupsDiscounts(cartData, excludedKeys);

    // Merge and deduplicate
    return [...new Set([...oneGroupGifts, ...twoGroupsGifts])];
  }

  /**
   * Removes invalid gift items from cart
   * @param {string[]} lineItemKeys - Array of line item keys to remove
   */
  async removeInvalidGifts(lineItemKeys) {
    if (!lineItemKeys || lineItemKeys.length === 0) return;
    
    if (!this.cartItemsElement) {
      return;
    }

    try {
      // Use clearCartItems method from CartItems class
      if (typeof this.cartItemsElement.clearCartItems === 'function') {
        this.cartItemsElement.clearCartItems(lineItemKeys);
      }
    } catch (error) {
    }
  }

  /**
   * Checks if there are any gift products in the DOM
   * @returns {boolean} True if gift products exist in cart UI
   */
  hasGiftsInDOM() {
    const giftItems = document.querySelectorAll(
      '.custom-cart-product-item[data-is-gift="true"]'
    );
    return giftItems.length > 0;
  }

  /**
   * Checks and identifies gifts from orphaned products
   * @param {Object} cartData - Current cart data
   * @returns {string[]} Array of line item keys to remove
   */
  checkOrphansPromoGiftByProduct(cartData) {
    const orphanedGiftKeys = this.identifyOrphanedGifts(cartData);
    return orphanedGiftKeys;
  }

  /**
   * Checks and identifies gifts from inactive promotions
   * @param {Object} cartData - Current cart data
   * @returns {string[]} Array of line item keys to remove
   */
  checkInactivePromoGifts(cartData) {
    const inactiveGiftKeys = this.identifyInactivePromoGifts(cartData);
    return inactiveGiftKeys;
  }

  /**
   * Checks and identifies gifts that exceed price limits
   * @param {Object} cartData - Current cart data
   * @param {string[]} excludedKeys - Keys to exclude from cart total calculation
   * @returns {string[]} Array of line item keys to remove
   */
  checkGiftsOverLimitPrice(cartData, excludedKeys = []) {
    const limitPriceGiftKeys = this.identifyGiftsOverLimitPrice(cartData, excludedKeys);
    return limitPriceGiftKeys;
  }

  /**
   * Merges multiple arrays of keys and removes duplicates
   * @param {...string[]} keyArrays - Multiple arrays of line item keys
   * @returns {string[]} Unique array of line item keys
   */
  mergeUniqueKeys(...keyArrays) {
    const allKeys = keyArrays.flat();
    return [...new Set(allKeys)];
  }

    getCartContainer() {
    return document.querySelector('cart-items') || 
           document.querySelector('cart-drawer-items');
  }

  getAllCartItems() {
    const container = this.getCartContainer();
    if (!container) return [];
    return container.querySelectorAll('.custom-cart-product-item');
  }

  findGiftsByCollections(collectionIds) {  
    const giftsFound = [];
    const allItems = this.getAllCartItems();

    allItems.forEach((item) => {
      const isGift = item.dataset.isGift === 'true';
      if (!isGift) return;
      
      const giftForCollection = item.dataset.giftForCollection;
      
      if (giftForCollection && collectionIds.includes(giftForCollection)) {        
        giftsFound.push(item);
      }
    });

    return giftsFound;
  }

  hasProductsFromCollection(collectionId, excludeIndex = null) {    
    const allItems = this.getAllCartItems();

    for (const item of allItems) {
      const removeButton = item.querySelector('cart-remove-button');
      if (!removeButton) continue;
      
      const itemIndex = removeButton.dataset.index;
      
      // Skip excluded item
      if (excludeIndex && itemIndex === excludeIndex.toString()) {        
        continue;
      }
      
      // Skip gifts
      if (item.dataset.isGift === 'true') {        
        continue;
      }
      
      // Check collections
      const itemCollections = item.dataset.cartProductCollections || '';
      if (!itemCollections) continue;
      
      const collectionIds = itemCollections.split(',').map(id => id.trim());
      
      if (collectionIds.includes(collectionId.toString())) {        
        return true;
      }
    }

    return false;
  }

  identifyOrphanedCollectionGifts(cartData) {
    const orphanedGiftKeys = [];

    cartData.items.forEach(item => {
      const isGift = item.properties?._is_gift === 'true' || 
                     item.properties?._is_gift === true;
      if (!isGift) return;

      const giftForCollection = item.properties?._gift_for_collection;
      if (!giftForCollection) return;

      // Uses DOM to check for products
      const hasCollectionProducts = this.hasProductsFromCollection(giftForCollection);
      
      if (!hasCollectionProducts) {
        orphanedGiftKeys.push(item.key);
      }
    });

    return orphanedGiftKeys;
  }

  checkOrphansPromoGiftByCollection(cartData) {
    return this.identifyOrphanedCollectionGifts(cartData);
  }

  /**
   * Main validation process
   * Checks cart for all invalid gifts and removes them in a single request
   */
  async validatePromos() {    
    if (!this.hasGiftsInDOM()) {      
      return;
    }

    const cartData = await this.fetchCartData();
    
    if (!cartData || !cartData.items || cartData.items.length === 0) {
      return;
    }
    
    const keysToRemove = [];

    // Check orphaned gifts (giftbyproduct without normal products)
    const orphanedKeys = this.checkOrphansPromoGiftByProduct(cartData);
    keysToRemove.push(orphanedKeys);

    // Check orphaned gifts (giftbycollection)
    const orphanedCollectionKeys = this.checkOrphansPromoGiftByCollection(cartData);
    keysToRemove.push(orphanedCollectionKeys);
    
    // Check gifts that no longer meet price limit requirements
    const limitPriceKeys = this.checkGiftsOverLimitPrice(cartData);
    keysToRemove.push(limitPriceKeys);

    // Check gifts from inactive promotions
    const inactivePromoKeys = this.checkInactivePromoGifts(cartData);
    keysToRemove.push(inactivePromoKeys);
    
    // Merge all keys and remove duplicates
    const uniqueKeysToRemove = this.mergeUniqueKeys(...keysToRemove);
    
    // Single removal operation for all invalid gifts
    if (uniqueKeysToRemove.length > 0) {      
      await this.removeInvalidGifts(uniqueKeysToRemove);
    }
  }
}

// Make GiftValidator globally accessible for cart.js
if (typeof window !== 'undefined') {  
  window.giftValidator = new GiftValidator();
}

/**
 * Initialize gift validation on page load
 */
function initGiftValidation() {
  const validator = window.giftValidator ||  new GiftValidator();
  validator.validatePromos();
}

// Execute after DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGiftValidation);
} else {
  initGiftValidation();
}